'use client';

import React, { useState, useEffect } from 'react';
import { classNames } from '@brahma/utils';
import useGlobalContext from '$hooks/use-global-context';
import useLocale from '$hooks/use-locale';
import { COMMON } from '$locale/constants';
import styles from './pwaInstallPrompt.module.scss';
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}
interface PWAInstallPromptProps {
  className?: string;
}
const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({
  className = ''
}) => {
  const {
    state
  } = useGlobalContext();
  const {
    t
  } = useLocale(COMMON);
  const isMobile = state.screen.isMobile;
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return;
      }

      // Check for iOS Safari
      if ((window.navigator as any).standalone === true) {
        setIsInstalled(true);
        return;
      }
    };
    checkIfInstalled();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstallPrompt(true);
    };

    // Listen for the appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const {
        outcome
      } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    } catch (error) {
      console.error('Error during installation:', error);
    }
  };
  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  // Don't show if already installed, no prompt available, or not on mobile
  if (isInstalled || !showInstallPrompt || !deferredPrompt || !isMobile) {
    return null;
  }
  return <div className={classNames(styles.mainContainer, className)} data-sentry-component="PWAInstallPrompt" data-sentry-source-file="index.tsx">
      <div className={styles.contentSection}>
        <div className={styles.iconContainer}>📱</div>
        <div className={styles.textSection}>
          <div className={styles.title}>{t('pwaInstall.title')}</div>
        </div>
      </div>

      <div className={styles.actionsSection}>
        <button onClick={handleDismiss} className={styles.dismissButton}>
          {t('pwaInstall.dismissButton')}
        </button>
        <button onClick={handleInstallClick} className={styles.installButton}>
          {t('pwaInstall.installButton')}
        </button>
      </div>
    </div>;
};
export default PWAInstallPrompt;